import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import config from '../../config/config';
import { supabase } from '../../config/supabase';

// Construct Netlify Functions URL
const NETLIFY_FUNCTIONS_URL = `${config.netlify.baseUrl}${config.netlify.functionsPath}`;

const getAuthHeaders = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('No authentication token found');
  }
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${session.access_token}`,
  };
};

export const fetchEmailRecipients = createAsyncThunk(
  'emailRecipients/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${NETLIFY_FUNCTIONS_URL}/email-recipients`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error.error || 'Error al cargar destinatarios');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching email recipients:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const createEmailRecipient = createAsyncThunk(
  'emailRecipients/create',
  async (recipientData, { rejectWithValue }) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${NETLIFY_FUNCTIONS_URL}/email-recipients`, {
        method: 'POST',
        headers,
        body: JSON.stringify(recipientData),
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error.error || 'Error al crear destinatario');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating email recipient:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateEmailRecipient = createAsyncThunk(
  'emailRecipients/update',
  async ({ id, ...updateData }, { rejectWithValue }) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${NETLIFY_FUNCTIONS_URL}/email-recipients/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error.error || 'Error al actualizar destinatario');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating email recipient:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const deleteEmailRecipient = createAsyncThunk(
  'emailRecipients/delete',
  async (id, { rejectWithValue }) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${NETLIFY_FUNCTIONS_URL}/email-recipients/${id}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error.error || 'Error al eliminar destinatario');
      }

      return id;
    } catch (error) {
      console.error('Error deleting email recipient:', error);
      return rejectWithValue(error.message);
    }
  }
);

const emailRecipientsSlice = createSlice({
  name: 'emailRecipients',
  initialState: {
    recipients: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmailRecipients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmailRecipients.fulfilled, (state, action) => {
        state.loading = false;
        state.recipients = action.payload;
      })
      .addCase(fetchEmailRecipients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createEmailRecipient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createEmailRecipient.fulfilled, (state, action) => {
        state.loading = false;
        state.recipients.unshift(action.payload);
      })
      .addCase(createEmailRecipient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateEmailRecipient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateEmailRecipient.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.recipients.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.recipients[index] = action.payload;
        }
      })
      .addCase(updateEmailRecipient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteEmailRecipient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteEmailRecipient.fulfilled, (state, action) => {
        state.loading = false;
        state.recipients = state.recipients.filter(r => r.id !== action.payload);
      })
      .addCase(deleteEmailRecipient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError } = emailRecipientsSlice.actions;
export default emailRecipientsSlice.reducer;
